"use client";
import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Add, Remove, ExpandMore } from "@mui/icons-material";
import { message } from "antd";
import axios from "axios";
import { useRouter } from "next/navigation";
import "./SelfPickupForm.scss";
import ProductSelectionModal from "./modules/ProductSelectionModal";
import VariantSelectionModal from "./modules/VariantSelectionModal";
import PaymentOptionsModal from "./modules/PaymentOptionsModal";
import CreateProductModal from "./modules/CreateProductModal";
import AccountStatus from "@/functions/Cookies/Cookies";

const SelfPickupForm = () => {
  const router = useRouter();
  const {
    loggedIn,
    comverse_customer_id,
    comverse_customer_email,
    comverse_customer_token,
  } = AccountStatus();
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetail, setProductDetail] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingProductDetail, setLoadingProductDetail] = useState(false);
  const [agentId, setAgentId] = useState("");
  const [vendorInputValue, setVendorInputValue] = useState("");
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [productInputValue, setProductInputValue] = useState("");
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [selectedInterestRate, setSelectedInterestRate] = useState(null);

  // Customer Information Form Fields
  const [customerName, setCustomerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");

  // Payment Information
  const [isBnpl, setIsBnpl] = useState(false);
  const [installmentPlan, setInstallmentPlan] = useState(null);

  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingProductDetail, setPendingProductDetail] = useState(null);
  const [customerInfoExpanded, setCustomerInfoExpanded] = useState(false);

  const [cardType, setCardType] = useState("");

  const storeId = 23;

  // Fetch vendors on component mount with initial load
  useEffect(() => {
    fetchVendors(""); // Initial load with empty search
  }, []);

  // Debounced search for vendors
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (vendorInputValue !== undefined) {
        fetchVendors(vendorInputValue);
      }
    }, 300); // 300ms delay for debouncing

    return () => clearTimeout(delayedSearch);
  }, [vendorInputValue]);

  const fetchVendors = async (searchTerm = "") => {
    try {
      setLoadingVendors(true);
      // Load more vendors for better search coverage
      let url = `${process.env.NEXT_PUBLIC_BACKEND}/storefront/b2b_vendor_list?storefront=${storeId}&page=1&limit=100`;

      // TODO: Check if your API supports search parameter
      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await axios.get(url);
      let vendorList =
        response.data?.results || response.data?.data || response.data || [];

      // Client-side filtering if API doesn't support search
      if (searchTerm && searchTerm.trim()) {
        vendorList = vendorList.filter(
          (vendor) =>
            vendor.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            vendor.title?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      setVendors(vendorList);
    } catch (error) {
      console.error("Failed to fetch vendors:", error);
      message.error("Failed to load vendors");
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchProducts = async (
    vendorId,
    categoryObj = null,
    searchTerm = ""
  ) => {
    try {
      setLoadingProducts(true);
      setProducts([]);
      setSelectedProduct(null);
      setProductDetail(null);
      setSelectedVariant(null);
      setQuantity(1);

      let url = `${process.env.NEXT_PUBLIC_BACKEND}/storefront/b2b_products?limit=50&storefront=${storeId}&vendor_id=${vendorId}`;

      // Add category ID if provided
      if (categoryObj && categoryObj.id) {
        url += `&category_id=${categoryObj.id}`;
      }

      // Add search parameter if searchTerm is provided
      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await axios.get(url);
      const productData =
        response.data?.data || response.data?.results || response.data || [];

      setProducts(productData);
    } catch (error) {
      console.error("Failed to fetch products:", error);
      message.error("Failed to load products");
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchProductDetail = async (productHandle) => {
    try {
      setLoadingProductDetail(true);
      setQuantity(1);

      // ?item_type=${selectedCategory.name}
      const response = await axios.get(
        `${process.env.NEXT_PUBLIC_BACKEND}/storefront/product/${productHandle}?storefront=${storeId}&item_type=${selectedCategory.name}&source=b2b_payments`
      );

      // Store the fetched product detail temporarily and show modal
      setPendingProductDetail(response.data);
      setShowProductModal(true);
    } catch (error) {
      console.error("Failed to fetch product detail:", error);
      message.error("Failed to load product details");
    } finally {
      setLoadingProductDetail(false);
    }
  };

  const handleVendorChange = (event, newValue) => {
    setSelectedVendor(newValue);
    setSelectedCategory(null);
    setProducts([]);
    setSelectedProduct(null);
    setProductDetail(null);
    setSelectedVariant(null);
    setProductInputValue("");

    if (!newValue) {
      setSelectedCategory(null);
    }
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue);
    setProducts([]);
    setSelectedProduct(null);
    setProductDetail(null);
    setSelectedVariant(null);
    setProductInputValue("");

    if (selectedVendor?.id) {
      fetchProducts(selectedVendor.id, newValue, "");
    }
  };

  const handleProductChange = (event, newValue) => {
    if (newValue?.isCreateOption) {
      // Open create product modal
      setShowCreateProductModal(true);
      return;
    }

    setSelectedProduct(newValue);
    if (newValue?.handle) {
      fetchProductDetail(newValue.handle);
    } else {
      setProductDetail(null);
      setSelectedVariant(null);
      setQuantity(1);
    }
  };

  // Handle product modal confirmation
  const handleProductModalConfirm = () => {
    setProductDetail(pendingProductDetail);

    setPendingProductDetail(null);
    setShowProductModal(false);

    // Check if product has multiple variants
    if (
      pendingProductDetail.variants &&
      pendingProductDetail.variants.length > 1
    ) {
      setShowVariantModal(true);
    } else {
      // Single variant, proceed to payment options
      const singleVariant = pendingProductDetail.variants[0];
      setSelectedVariant(singleVariant);
      setShowPaymentModal(true);
    }
  };

  // Handle product modal cancellation
  const handleProductModalCancel = () => {
    setSelectedProduct(null);
    setPendingProductDetail(null);
    setShowProductModal(false);
    // Clear any related state
    setSelectedVariant(null);
    setIsBnpl(false);
    setInstallmentPlan(null);
  };

  // Handle variant selection
  const handleVariantConfirm = (variant) => {
    setSelectedVariant(variant);
    setShowVariantModal(false);
    setShowPaymentModal(true);
  };

  // Handle variant modal cancellation
  const handleVariantModalCancel = () => {
    setShowVariantModal(false);
    // Reset product selection completely
    setSelectedProduct(null);
    setProductDetail(null);
    setSelectedVariant(null);
    setIsBnpl(false);
    setInstallmentPlan(null);
  };

  // Handle payment options confirmation
  const handlePaymentOptionsConfirm = (paymentData) => {
    setSelectedVariant(paymentData.variant);
    setIsBnpl(paymentData.isBnpl);
    setInstallmentPlan(paymentData.installmentPlan);
    setShowPaymentModal(false);
  };

  // Handle payment modal cancellation
  const handlePaymentModalCancel = () => {
    setShowPaymentModal(false);
    // Go back to variant selection if multiple variants, otherwise reset product
    if (
      productDetail &&
      productDetail.variants &&
      productDetail.variants.length > 1
    ) {
      setSelectedVariant(null);
      setIsBnpl(false);
      setInstallmentPlan(null);
      setShowVariantModal(true);
    } else {
      setSelectedProduct(null);
      setProductDetail(null);
      setSelectedVariant(null);
      setIsBnpl(false);
      setInstallmentPlan(null);
    }
  };

  // Handle vendor input change for search
  const handleVendorInputChange = (event, newInputValue) => {
    setVendorInputValue(newInputValue);
  };

  // Handle product input change for search
  const handleProductInputChange = (event, newInputValue) => {
    setProductInputValue(newInputValue);
    if (selectedVendor?.id) {
      clearTimeout(window.productSearchTimeout);
      window.productSearchTimeout = setTimeout(() => {
        fetchProducts(selectedVendor.id, selectedCategory, newInputValue);
      }, 300);
    }
  };

  const handleQuantityChange = (type) => {
    if (type === "increase") {
      const maxQuantity = selectedVariant?.inventory_quantity || 999;
      if (quantity < maxQuantity) {
        setQuantity((prev) => prev + 1);
      }
    } else if (type === "decrease" && quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  const submitPaymentRequest = async () => {
    if (
      !selectedVariant ||
      !customerName ||
      !phoneNumber ||
      !email ||
      !address ||
      !paymentMethod
    ) {
      message.error("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        category_id: selectedCategory?.id || productDetail?.category?.[0]?.id,
        variant_price_inventory: selectedVariant.variant_detail?.variant_id,
        quantity: quantity,
        is_bnpl: isBnpl,
        installment_plan: installmentPlan,
        customer_name: customerName,
        phone_number: phoneNumber,
        email: email,
        address: address,
        discount_code: discountCode,
        payment_method: paymentMethod,
        card_type: cardType,
        agent_id: agentId,
        interest_rate: selectedInterestRate || 0,
      };

      const headers = {
        "Content-Type": "application/json",
      };

      if (comverse_customer_token) {
        headers.Authorization = comverse_customer_token;
      }

      const response = await axios.post(
        `${process.env.NEXT_PUBLIC_BACKEND}/order/add_b2b_transaction_detail`,
        payload,
        {
          headers: {
            ...headers,
          },
        }
      );

      if (response.data?.cart_id) {
        const cartId = response.data.cart_id;
        window.location.href = `${process.env.NEXT_PUBLIC_BACKEND}/payment/b2b_payment_request?cart_id=${cartId}`;
      } else {
        message.error("Cart ID not received from server");
        setLoading(false);
      }
    } catch (error) {
      console.error("Failed to submit payment request:", error);
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        "Failed to submit payment request";
      message.error(errorMessage);
      setLoading(false);
    }
  };

  const getSelectedVariantPrice = () => {
    if (!selectedVariant?.variant_detail) return "N/A";
    const variantDetail = selectedVariant.variant_detail;
    return (
      variantDetail.discounted_price || variantDetail.original_price || "N/A"
    );
  };

  const isFormValid = () => {
    return (
      selectedVariant &&
      customerName.trim() &&
      phoneNumber.trim() &&
      email.trim() &&
      address.trim() &&
      paymentMethod &&
      quantity > 0
    );
  };

  // Get available categories for selected vendor
  const getVendorCategories = () => {
    return selectedVendor?.categories || [];
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (window.productSearchTimeout) {
        clearTimeout(window.productSearchTimeout);
      }
    };
  }, []);

  const handleCreateProductModalClose = () => {
    setShowCreateProductModal(false);
  };

  const handleCreateProductSuccess = (newProduct) => {
    setShowCreateProductModal(false);
    if (selectedVendor?.id) {
      fetchProducts(selectedVendor.id, selectedCategory, "");
    }
    message.success("Product created successfully!");
  };

  const getProductOptions = () => {
    const createOption = {
      id: "create-new",
      title: "Create Product",
      isCreateOption: true,
    };
    return [createOption, ...products];
  };

  console.log("Selected Product", selectedVariant);

  return (
    <div className="retail-outlet-form">
      <Card className="form-card">
        <CardContent>
          <Typography variant="h4" component="h1" className="form-heading">
            Digital Payments
          </Typography>

          {/* Vendor Selection */}
          <Box className="form-section">
            <Typography variant="h6" className="field-label">
              Select Seller *
            </Typography>
            <Autocomplete
              options={vendors}
              getOptionLabel={(option) => option.name || option.title || ""}
              value={selectedVendor}
              onChange={handleVendorChange}
              onInputChange={handleVendorInputChange}
              inputValue={vendorInputValue}
              loading={loadingVendors}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and choose a seller..."
                  variant="outlined"
                  className="autocomplete-field"
                  required
                />
              )}
              className="vendor-autocomplete"
              noOptionsText={
                vendorInputValue ? "No sellers found" : "Type to search sellers"
              }
            />
          </Box>

          {/* Category Selection */}
          <Box className="form-section">
            <Typography variant="h6" className="field-label">
              Select Category
            </Typography>
            <Autocomplete
              options={getVendorCategories()}
              getOptionLabel={(option) => option.name || ""}
              value={selectedCategory}
              onChange={handleCategoryChange}
              disabled={!selectedVendor}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedVendor
                      ? "Choose a category (optional)..."
                      : "Select a seller first"
                  }
                  variant="outlined"
                  className="autocomplete-field"
                />
              )}
              className="category-autocomplete"
              noOptionsText="No categories available"
            />
          </Box>

          {/* Product Selection */}
          <Box className="form-section">
            <Typography variant="h6" className="field-label">
              Select Products *
            </Typography>
            <Autocomplete
              // options={products}
              options={getProductOptions()}
              getOptionLabel={(option) => option.title || ""}
              value={selectedProduct}
              onChange={handleProductChange}
              onInputChange={handleProductInputChange}
              inputValue={productInputValue}
              loading={loadingProducts}
              disabled={!selectedVendor}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    selectedVendor
                      ? "Search and choose a product..."
                      : "Select a seller first"
                  }
                  variant="outlined"
                  className="autocomplete-field"
                  required
                />
              )}
              className="product-autocomplete"
              noOptionsText={
                selectedVendor
                  ? productInputValue
                    ? "No products found"
                    : "Type to search products"
                  : "Select a seller first"
              }
            />
            {selectedCategory && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 1, display: "block" }}
              >
                Filtering by category: {selectedCategory.name}
              </Typography>
            )}
          </Box>

          {/* Selected Product Details */}
          {selectedVariant && (
            <Box className="form-section">
              <Typography variant="h6" className="field-label">
                Selected Product Details
              </Typography>
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {productDetail.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Variant: {selectedVariant.title}
                </Typography>
                <Typography variant="h6" sx={{ mt: 1 }}>
                  PKR {getSelectedVariantPrice()}
                </Typography>
                {isBnpl && installmentPlan && (
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ mt: 1 }}
                  >
                    Payment Plan: {installmentPlan}
                  </Typography>
                )}
              </Card>
            </Box>
          )}

          {/* Quantity Selection */}
          <Box className="form-section">
            <Box className="quantity-section">
              <Typography variant="body1" className="quantity-label">
                Select Quantity: *
              </Typography>
              <Box className="quantity-controls">
                <IconButton
                  onClick={() => handleQuantityChange("decrease")}
                  disabled={quantity <= 1 || !selectedVariant}
                  className="quantity-btn"
                >
                  <Remove />
                </IconButton>
                <TextField
                  value={quantity}
                  variant="outlined"
                  // disabled
                  onChange={(e) => setQuantity(e.target.value)}
                  type="number"
                  className="quantity-input"
                  inputProps={{ style: { textAlign: "center" } }}
                />
                <IconButton
                  onClick={() => handleQuantityChange("increase")}
                  disabled={!selectedVariant}
                  className="quantity-btn"
                >
                  <Add />
                </IconButton>
              </Box>
            </Box>
          </Box>

          <Box className="form-section">
            <Typography variant="h6" className="field-label">
              Discount Code
            </Typography>
            <FormControl>
              <TextField
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                variant="outlined"
                fullWidth
                placeholder="Enter discount code"
                required
                className="text-field"
              />
            </FormControl>
          </Box>

          <Box className="form-section">
            <Typography variant="h6" className="field-label">
              Payment Method *
            </Typography>
            <FormControl fullWidth variant="outlined" required>
              <InputLabel>Select Payment Method</InputLabel>
              <Select
                // value={paymentMethod}
                // onChange={(e) => setPaymentMethod(e.target.value)}
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                  setCustomerInfoExpanded(true);
                }}
                label="Select Payment Method"
                className="text-field"
              >
                <MenuItem value="Credit / Debit Card">
                  Credit / Debit Card
                </MenuItem>
                <MenuItem value="Cash on Delivery">Cash on Delivery</MenuItem>
                <MenuItem value="Raast QR">Raast QR</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Accordion
            className="customer-info-accordion"
            expanded={customerInfoExpanded}
            onChange={(_e, isExpanded) => setCustomerInfoExpanded(isExpanded)}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              aria-controls="customer-info-content"
              id="customer-info-header"
            >
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Customer Information
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="form-section">
                <Typography variant="h6" className="field-label">
                  Customer Name *
                </Typography>
                <TextField
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  variant="outlined"
                  fullWidth
                  placeholder="Enter customer name"
                  required
                  className="text-field"
                />
              </Box>

              <Box className="form-section">
                <Typography variant="h6" className="field-label">
                  Phone Number *
                </Typography>
                <TextField
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  variant="outlined"
                  fullWidth
                  placeholder="Enter phone number"
                  required
                  className="text-field"
                />
              </Box>

              <Box className="form-section">
                <Typography variant="h6" className="field-label">
                  Email Address *
                </Typography>
                <TextField
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  variant="outlined"
                  fullWidth
                  type="email"
                  placeholder="Enter email address"
                  required
                  className="text-field"
                />
              </Box>

              <Box className="form-section">
                <Typography variant="h6" className="field-label">
                  Address *
                </Typography>
                <TextField
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter complete address"
                  required
                  className="text-field"
                />
              </Box>
              <Box className="form-section">
                <Typography variant="h6" className="field-label">
                  Agent Id
                </Typography>
                <TextField
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  variant="outlined"
                  fullWidth
                  type="text"
                  placeholder="Enter Agent Id"
                  className="text-field"
                />
              </Box>
            </AccordionDetails>
          </Accordion>

          {/* Submit Button */}
          <Box className="form-section">
            <Button
              variant="contained"
              size="large"
              onClick={submitPaymentRequest}
              disabled={!isFormValid() || loading}
              className="checkout-btn"
              fullWidth
            >
              {loading ? "Submitting Payment Request..." : "Pay Now"}
            </Button>
          </Box>

          {/* Order Summary */}
          {selectedVariant && (
            <Box className="product-summary">
              <Typography variant="h6">Order Summary</Typography>
              <Typography variant="body2">
                <strong>Product:</strong> {selectedVariant.title}
              </Typography>
              <Typography variant="body2">
                <strong>Seller:</strong>{" "}
                {selectedVendor?.name || selectedVendor?.title}
              </Typography>
              {selectedCategory && (
                <Typography variant="body2">
                  <strong>Category:</strong> {selectedCategory.name}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Price:</strong> PKR {getSelectedVariantPrice()}
              </Typography>
              <Typography variant="body2">
                <strong>Quantity:</strong> {quantity}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: "bold", mt: 1 }}>
                <strong>Total:</strong> PKR{" "}
                {(
                  parseFloat(getSelectedVariantPrice().replace(/,/g, "")) *
                  quantity
                ).toLocaleString()}
              </Typography>
              {isBnpl && installmentPlan && (
                <Typography variant="body2" color="success.main">
                  <strong>Payment Plan:</strong> {installmentPlan}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <ProductSelectionModal
        open={showProductModal}
        onClose={handleProductModalCancel}
        onConfirm={handleProductModalConfirm}
        productDetail={pendingProductDetail}
        selectedVendor={selectedVendor}
        selectedCategory={selectedCategory}
      />

      <VariantSelectionModal
        open={showVariantModal}
        onClose={handleVariantModalCancel}
        onConfirm={handleVariantConfirm}
        productDetail={productDetail}
      />

      <PaymentOptionsModal
        open={showPaymentModal}
        onClose={handlePaymentModalCancel}
        onConfirm={handlePaymentOptionsConfirm}
        setCardType={setCardType}
        selectedVariant={selectedVariant}
        productDetail={productDetail}
        selectedInterestRate={selectedInterestRate}
        setSelectedInterestRate={setSelectedInterestRate}
      />

      <CreateProductModal
        open={showCreateProductModal}
        onClose={handleCreateProductModalClose}
        onSuccess={handleCreateProductSuccess}
        selectedVendor={selectedVendor}
        selectedCategory={selectedCategory}
        storeId={storeId}
      />
    </div>
  );
};

export default SelfPickupForm;
